import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { saveAs } from 'file-saver';

interface ResultPPReportState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    filter: {
        dZoneGroups: any;
        targetDates: Date[];
    };

    documents: any[];
}

const state: ResultPPReportState = {
    loading: false,
    loaded: false,
    fatalError: false,

    filter: {
        dZoneGroups: null,
        targetDates: [new Date(), new Date()],
    },

    documents: [],
};

function buildFilter(params: any) {
    const queryParams: any = {}

    if (params.targetDates) {
        queryParams.date_from = moment(params.targetDates[0]).format('YYYY-MM-DD');
        queryParams.date_to = moment(params.targetDates[1]).add(1, 'day').format('YYYY-MM-DD')
    }

    if (params.dZoneGroups) {
        queryParams.delivery_zone_id = params.dZoneGroups;
    }
    if (params.employeeId) {
        queryParams.employee_id = params.employeeId;
    }

    return queryParams;
}

export const REPORT_DATA = 'REPORT_DATA';

export const actions: ActionTree<ResultPPReportState, RootState> = {

    loadData({ commit, state, dispatch }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {

            if (Object.values(params).length > 0) {
                commit('START_LOADING');

                vm.$db([
                    vm.$q(REPORT_DATA)
                        .raw('documents.list')
                        .action({
                            columns: {
                                id: 's.id',
                                shift_date: 's.shift_date',
                                times_of_day: 's.times_of_day',
                                number: 'spv.number',
                                rotates: 'spv.rotates',
                                base_name: 'vb.name',
                                starter_type_name: 'vst.name',
                                starter_type_temp: 'vst.temp',
                                extract_time: 'sfv.extract_time',
                                ferment_time: 'sjv.ferment_time',
                                mark_no_incident: 'vbm.no_incident',
                                mark_water_state: 'vbm.water_state',
                                mark_no_fat: 'vbm.no_fat',
                                mark_normal_acidity: 'vbm.normal_acidity',
                                clots: {
                                    $array_agg: {
                                        $obj: {
                                            temp: 'scv.temp',
                                            acidity: 'scv.acidity',
                                        }
                                    }
                                },
                                base_params: {
                                    $obj: {
                                        ph_1h_range: "vst.ph_1h_range",
                                        ph_2h_range: "vst.ph_2h_range",
                                        ph_3h_range: "vst.ph_3h_range",
                                        ph_4h_range: "vst.ph_4h_range",
                                        ph_5h_range: "vst.ph_5h_range",
                                        ph_6h_range: "vst.ph_6h_range",
                                        ph_7h_range: "vst.ph_7h_range",
                                        ph_8h_range: "vst.ph_8h_range",
                                        ph_9h_range: "vst.ph_9h_range",
                                    }
                                  },
                            },
                            from: "s:shifts",
                            from_as: "s",
                            joins: [
                                {
                                    join: 's:shift_plan_vdp',
                                    type: 'left',
                                    as:   'spv',
                                    on: {
                                      $eq: ['spv.shift_id', 's.id']
                                    }
                                },
                                {
                                    join: 's:shift_fact_vdp',
                                    type: 'left',
                                    as:   'sfv',
                                    on: {
                                      $eq: ['sfv.shift_plan_id', 'spv.id']
                                    }
                                },
                                {
                                    join: 's:shift_journal_vdp',
                                    type: 'left',
                                    as:   'sjv',
                                    on: {
                                      $eq: ['sjv.fact_id', 'sfv.id']
                                    }
                                },
                                {
                                    join: 's:voc_bases',
                                    type: 'left',
                                    as:   'vb',
                                    on: {
                                      $eq: ['spv.base_id', 'vb.id']
                                    }
                                },
                                {
                                    join: 's:voc_starter_types',
                                    type: 'left',
                                    as:   'vst',
                                    on: {
                                      $eq: ['spv.starter_type_id', 'vst.id']
                                    }
                                },
                                {
                                    join: 's:view_base_mark',
                                    // type: 'left',
                                    as:   'vbm',
                                    on: {
                                      $eq: ['vbm.id', 'sfv.id']
                                    }
                                },
                                {
                                    join: 's:shift_clot_vdp',
                                    // type: 'left',
                                    as:   'scv',
                                    on: {
                                      $and: [
                                        {$eq: ['scv.plan_id', 'spv.id']},
                                        {$eq: ['vbm.datetime', 'scv.datetime']}
                                      ]
                                    }
                                },
                            ],
                            page_size: 10000,
                            where: {
                                $and: [
                                    {$gte: ['s.shift_date', {$: params.targetDates[0]}]},
                                    {$lte: ['s.shift_date', {$: params.targetDates[1]}]}
                                ]
                            },
                            groups: [
                                's.id',
                                's.shift_date',
                                's.times_of_day',
                                'spv.number',
                                'spv.rotates',
                                'vb.name',
                                'vst.name',
                                'vst.temp',
                                'sfv.extract_time',
                                'sjv.ferment_time',
                                'vbm.no_incident',
                                'vbm.water_state',
                                'vbm.no_fat',
                                'vbm.normal_acidity',
                                "vst.ph_1h_range",
                                "vst.ph_2h_range",
                                "vst.ph_3h_range",
                                "vst.ph_4h_range",
                                "vst.ph_5h_range",
                                "vst.ph_6h_range",
                                "vst.ph_7h_range",
                                "vst.ph_8h_range",
                                "vst.ph_9h_range",
                            ]
                        })
                ]).then(((result: ServerResult) => {
                    const content = result.getOperation(REPORT_DATA).getContent() 
                    commit('SET_REPORT_DATA', content.documents.map((item: any) => {
                        item.fermentation_time = null
                        if (item.extract_time && item.ferment_time) {
                            const _start = moment('1970-01-01 00:00:00').add(moment.duration(moment(item.extract_time).diff(moment(item.ferment_time))))
                            item.fermentation_time = _start.format('HH:mm:ss')
                            let _hour = _start.hours()
                            if (_start.minutes() > 30) {
                                _hour += 1
                            }
                            switch (_hour) {
                                case 1: 
                                item.ph_h_range = item.base_params.ph_1h_range;
                                break;
                                case 2: 
                                item.ph_h_range = item.base_params.ph_2h_range;
                                break;
                                case 3: 
                                item.ph_h_range = item.base_params.ph_3h_range;
                                break;
                                case 4: 
                                item.ph_h_range = item.base_params.ph_4h_range;
                                break;
                                case 5: 
                                item.ph_h_range = item.base_params.ph_5h_range;
                                break;
                                case 6: 
                                item.ph_h_range = item.base_params.ph_6h_range;
                                break;
                                case 7: 
                                item.ph_h_range = item.base_params.ph_7h_range;
                                break;
                                case 8: 
                                item.ph_h_range = item.base_params.ph_8h_range;
                                break;
                                case 9: 
                                item.ph_h_range = item.base_params.ph_9h_range;
                                break;
                                
                                default: 
                                item.ph_h_range = item.base_params.ph_9h_range;
                                break;
                            }
                        }
                        let temps: any[] = [];
                        let acidities: any[] = [];
                        if (item.clots) {
                            item.clots.map((item: any) => {
                                temps.push(item.temp)
                                acidities.push(item.acidity)
                            })
                        }
                        temps = temps.sort()
                        acidities = acidities.sort()
                        item.temp_range = temps.length ? [temps[0], temps[temps.length - 1]] : null
                        item.acidity_range = temps.length ? [acidities[0], acidities[acidities.length - 1]] : null
                        return item
                    }));
                    commit('LOADED_SUCCESS');
                    resolve();
                }));
            } else {
                resolve();
            }
        })
    },

    exportDataAsXLSX({rootState}, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {
            vm.$db([
                vm.$q('EXPORT')
                .raw('reports.documents.export')
                .action({ ...buildFilter(params), format: 'xlsx'})
            ]).then(((result: ServerResult) => {
                const exportOpContent = result.getOperation('EXPORT').getContent();
                const fToken = exportOpContent.token;
                const aToken = (rootState as any).ubic.token
                const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
                saveAs(fileUrl, 'export.xlsx')

                resolve();
            }));
        })
    },


};

export const mutations: MutationTree<ResultPPReportState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.fatalError = true;
    },

    SET_REPORT_DATA(state, data) {
        state.documents = data;
    },
};

const getters = {
  exportData(state: ResultPPReportState) {
    const csvContent: any[] = [];
    const header = [
      '№ партии после мойки',
      'Дата и № партии',
      'База',
      'Закваска',
      'Рекомендуемая t заквашивания',
      'Продолжительность сквашивания, ч',
      'pH',
      't, C',
    ]

    state.documents.map((item: any, idx: number) => {
      const row: any[] = [];
      row.push(idx + 1)
      row.push(moment(item.shift_date).format('DD.MM.YYYY') + ' ' + item.number)
      row.push(item.base_name)
      row.push(item.starter_type_name)
      row.push(item.starter_type_temp)
      row.push(item.fermentation_time)
      row.push(item.acidity_range.join(' - '))
      row.push(item.temp_range.join(' - '))
    //     })
      csvContent.push(row)
    })
    return [
        header,
        ...csvContent
    ]
  }
};

export const STORE_KEY = '$_result_pp_report';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
